import React, { useState, useEffect, useRef } from 'react';
import { Mail, Phone, MapPin, Clock, Send, CheckCircle, User, MessageSquare } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { mockData } from '../mock';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [visibleSections, setVisibleSections] = useState(new Set());
  const sectionRefs = useRef({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections(prev => new Set([...prev, entry.target.id]));
          }
        });
      },
      { threshold: 0.1 }
    );

    Object.values(sectionRefs.current).forEach(ref => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleServiceChange = (value) => {
    setFormData(prev => ({ ...prev, service: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Mock form submission using mock data
      const result = await mockData.submitContactForm(formData);
      
      if (result.success) {
        setSubmitSuccess(true);
        setFormData({ name: '', email: '', phone: '', service: '', message: '' });
        setTimeout(() => setSubmitSuccess(false), 5000);
      }
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: Mail,
      title: "Email Us",
      details: "info@brainwavesventures.com",
      subtitle: "We'll respond within 24 hours"
    },
    {
      icon: Phone,
      title: "Call Us",
      details: "+1 (555) 123-4567",
      subtitle: "Mon-Fri, 9AM-6PM EST"
    },
    {
      icon: MapPin,
      title: "Visit Us",
      details: "123 Wellness Street, Mind City, MC 12345",
      subtitle: "By appointment only"
    },
    {
      icon: Clock,
      title: "Office Hours",
      details: "Monday - Friday: 9AM - 6PM EST",
      subtitle: "Weekend emergency consultations available"
    }
  ];

  const services = [
    "Individual Therapy",
    "Group Coaching",
    "International Retreats",
    "Online Workshops",
    "General Inquiry"
  ];

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Hero Section */}
      <section className="py-8 lg:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-4">
            <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6">
              Get In <span className="text-black">Touch</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Ready to begin your journey of healing, growth, and connection? We're here to support you every step of the way. Reach out today for a free consultation.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form & Image */}
      <section 
        id="contact-form"
        ref={el => sectionRefs.current['contact-form'] = el}
        className={`pt-4 pb-20 transition-all duration-1500 delay-200 ${
          visibleSections.has('contact-form')
            ? 'opacity-100 translate-y-0'
            : 'opacity-0 translate-y-8'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center">
            {/* Contact Form */}
            <div className="w-full max-w-2xl">
              <Card className="shadow-xl">
                <CardHeader>
                  <CardTitle className="text-2xl font-bold text-gray-900 flex items-center">
                    <MessageSquare className="h-6 w-6 mr-3" style={{ color: 'rgba(50, 57, 241, 1)' }} />
                    Send Us a Message
                  </CardTitle>
                  <p className="text-gray-600">
                    Fill out the form below and we'll get back to you within 24 hours.
                  </p>
                </CardHeader>
                
                <CardContent>
                  {submitSuccess && (
                    <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center">
                      <CheckCircle className="h-5 w-5 text-green-600 mr-3" />
                      <p className="text-green-800 font-medium">
                        Thank you for your message! We'll get back to you within 24 hours.
                      </p>
                    </div>
                  )}
                  
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="name" className="text-sm font-medium text-gray-700">
                          Full Name *
                        </Label>
                        <Input
                          id="name"
                          name="name"
                          type="text"
                          required
                          value={formData.name}
                          onChange={handleInputChange}
                          className="py-3"
                          placeholder="Your full name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-sm font-medium text-gray-700">
                          Email Address *
                        </Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          required
                          value={formData.email}
                          onChange={handleInputChange}
                          className="py-3"
                          placeholder="your.email@example.com"
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="phone" className="text-sm font-medium text-gray-700">
                          Phone Number
                        </Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleInputChange}
                          className="py-3"
                          placeholder="(555) 123-4567"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="service" className="text-sm font-medium text-gray-700">
                          Service of Interest
                        </Label>
                        <Select onValueChange={handleServiceChange}>
                          <SelectTrigger className="py-3">
                            <SelectValue placeholder="Select a service" />
                          </SelectTrigger>
                          <SelectContent>
                            {services.map((service, index) => (
                              <SelectItem key={index} value={service}>
                                {service}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="message" className="text-sm font-medium text-gray-700">
                        Message *
                      </Label>
                      <Textarea
                        id="message"
                        name="message"
                        required
                        value={formData.message}
                        onChange={handleInputChange}
                        rows={6}
                        className="resize-none"
                        placeholder="Tell us about your needs and how we can help you..."
                      />
                    </div>
                    
                    <button 
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full px-4 py-2 border rounded-full text-sm transition-colors duration-200" 
                      style={{ 
                        borderColor: '#6a6acb', 
                        color: '#6a6acb',
                        backgroundColor: 'transparent'
                      }} 
                      onMouseEnter={(e) => {
                        if (!isSubmitting) {
                          e.target.style.backgroundColor = '#6a6acb';
                          e.target.style.color = 'white';
                        }
                      }} 
                      onMouseLeave={(e) => {
                        if (!isSubmitting) {
                          e.target.style.backgroundColor = 'transparent';
                          e.target.style.color = '#6a6acb';
                        }
                      }}
                    >
                      {isSubmitting ? (
                        <div className="flex items-center justify-center">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
                          Sending...
                        </div>
                      ) : (
                        <div className="flex items-center justify-center">
                          <Send className="mr-2 h-4 w-4" />
                          Send Message →
                        </div>
                      )}
                    </button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section 
        id="faq"
        ref={el => sectionRefs.current['faq'] = el}
        className={`py-20 bg-gray-50 transition-all duration-1500 delay-200 ${
          visibleSections.has('faq')
            ? 'opacity-100 translate-y-0'
            : 'opacity-0 translate-y-8'
        }`}
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Common <span className="text-gray-900">Questions</span>
            </h2>
            <p className="text-xl text-gray-600">
              Here are answers to some frequently asked questions about getting started.
            </p>
          </div>
          
          <div className="space-y-6">
            {[
              {
                question: "How quickly can I schedule my first session?",
                answer: "We typically can schedule your first consultation within 48-72 hours of your initial contact. For urgent needs, we offer same-day emergency consultations."
              },
              {
                question: "Do you offer online sessions?",
                answer: "Yes! We provide both in-person and secure online sessions via our encrypted platform. This allows us to support clients globally while maintaining the highest standards of confidentiality."
              },
              {
                question: "What should I expect from my first consultation?",
                answer: "Your free 15-minute consultation is a chance for us to understand your needs and goals, while you get to know our approach. There's no pressure - it's simply an opportunity to see if we're the right fit for your journey."
              },
              {
                question: "Are your services covered by insurance?",
                answer: "We work with several insurance providers and offer flexible payment options. During your consultation, we'll help you understand your coverage and explore all available options."
              }
            ].map((faq, index) => (
              <Card key={index} className="p-6">
                <CardContent>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">{faq.question}</h3>
                  <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Emergency Contact Section */}
      <section 
        id="emergency"
        ref={el => sectionRefs.current['emergency'] = el}
        className={`py-16 bg-red-50 border-t border-red-100 transition-all duration-1500 delay-200 ${
          visibleSections.has('emergency')
            ? 'opacity-100 translate-y-0'
            : 'opacity-0 translate-y-8'
        }`}
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">
            Crisis Support
          </h3>
          <p className="text-gray-700 mb-6 leading-relaxed">
            If you're experiencing a mental health emergency, please reach out for immediate help:
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <div className="bg-white p-4 rounded-lg shadow border border-red-200">
              <p className="font-semibold text-gray-900">National Suicide Prevention Lifeline</p>
              <p className="text-red-600 font-bold text-lg">988 or 1-800-273-8255</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow border border-red-200">
              <p className="font-semibold text-gray-900">Crisis Text Line</p>
              <p className="text-red-600 font-bold text-lg">Text HOME to 741741</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;